import React from 'react';
import { useLocale } from 'locale';
import ScrollIntoView from 'scroll-into-view';
import { ControlButtonGroup } from 'ds/components/button';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { AUTO_SCROLL_DURATION, Section } from 'components/navigation/subheader/SectionsNavigation';
import ScrollableSectionsNavigation from 'components/scrollable-sections-navigation';
import { COLLAPSED_STICKY_HEIGHT, EXPANDED_STICKY_HEIGHT, SectionWrapper } from 'screens/DeveloperPage/styled';
import Filters from 'screens/DeveloperPage/components/filters';
import { SpacingBlock } from 'components/spacing-block';
import * as Styled from './styled';
import { HEADER_HEIGHT } from 'ds/components/Layout';
import { projectFilterSections } from 'screens/DeveloperPage/helpers';


interface ProjectPageNavigationProps {
  hasProjects: boolean;
  showBorder: boolean;
  activeSection: Section;
  sections: Section[];
}

const ProjectPageNavigation: React.FC<ProjectPageNavigationProps> = (props) => {
  const {
    activeSection,
    hasProjects,
    showBorder,
    sections,
  } = props;
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() === 1;

  if (!sections.length) return null;

  const items = sections.map(item => ({ label: t('developerPage.subMenu.item', { item }), value: item }));
  const showFilters = projectFilterSections.has(activeSection) && hasProjects;
  const activeOption = activeSection === Section.PROJECTS_MAP ? Section.PROJECTS_MAP : Section.PROJECTS_LIST;
  const topOffset = isMobile ? HEADER_HEIGHT * 2 : COLLAPSED_STICKY_HEIGHT + HEADER_HEIGHT;

  const topOffsetMap = isMobile ? null : {
    [sections[0]]: EXPANDED_STICKY_HEIGHT + HEADER_HEIGHT,
    ...(sections.slice(1).reduce((res, section) => ({
      ...res,
      [section]: COLLAPSED_STICKY_HEIGHT + HEADER_HEIGHT,
    }), {})),
  };

  const scrollSettings = {
    time: AUTO_SCROLL_DURATION,
    align: {
      topOffset,
      top: 0,
    },
  };
  const toggleClick = (section: Section) => () => {
    if (section !== activeSection) {
      const el = document.querySelector<HTMLElement>(`[data-scroll-spy-id="${section}"]`);
      ScrollIntoView(el, scrollSettings);
    }
  };
  const toggleOptions = [ {
    value: Section.PROJECTS_LIST,
    label: t('developerPage.navigation.listMapToggle', { isMap: false }),
    onClick: toggleClick(Section.PROJECTS_LIST),
  }, {
    value: Section.PROJECTS_MAP,
    label: t('developerPage.navigation.listMapToggle', { isMap: true }),
    onClick: toggleClick(Section.PROJECTS_MAP),
  } ];

  const separator = <Styled.NavSeparator />;

  return (
    <Styled.Root hasBorderBottom={showBorder || !hasProjects}>
      <SectionWrapper noTopMargin>
        <Styled.Wrapper>
          <Styled.NavigationWrapper>
            <ScrollableSectionsNavigation
              separator={separator}
              items={items}
              activeItem={activeSection}
              topOffset={topOffset}
              topOffsetMap={topOffsetMap}
              scrollOnClick={sections.length > 1}
            />
          </Styled.NavigationWrapper>
          {showFilters ? (
            <ShowAt from={3}>
              <Styled.FiltersWrapper>
                <SpacingBlock mLeft={4}>
                  <Filters />
                </SpacingBlock>
                <ControlButtonGroup
                  options={toggleOptions}
                  currentValue={activeOption}
                  selectedMode="priority"
                />
              </Styled.FiltersWrapper>
            </ShowAt>
          ) : null}
        </Styled.Wrapper>
      </SectionWrapper>
    </Styled.Root>
  );
};


export default ProjectPageNavigation;
